// Availability helpers shared by product cards (Home, ProductCard) and the product detail page.
// Stock is per-variation on product.pricing[]; a combo is available when it has regular stock
// OR an active pre-order.
//
// Note on the data: the grouped/list endpoints often OMIT `availableStock` for untouched
// variations (only `initialStock` is present). So availability falls back to initialStock, and
// when stock is entirely unknown we assume available to avoid falsely flagging in-stock items.

const isEntryActive = (entry) => (entry?.productState ?? 'Active') !== 'Inactive';

const hasRegularStock = (entry) => {
  if (typeof entry.availableStock === 'number') return entry.availableStock > 0;
  if (typeof entry.initialStock === 'number') return entry.initialStock > 0;
  return true; // stock unknown — assume available rather than show a false "out of stock"
};

const isPricingEntryAvailable = (entry, now = new Date()) => {
  if (!entry) return false;
  if (hasRegularStock(entry)) return true;
  const preOrderEnd = entry.preOrderdateEnd ? new Date(entry.preOrderdateEnd) : null;
  const preOrderActive = preOrderEnd && now <= preOrderEnd;
  return !!(entry.isPreOrderEnabled && preOrderActive && entry.preOrderStockLeft > 0);
};

// True only when the product has pricing rows and every (active) variation combo is unavailable.
export const isProductOutOfStock = (product) => {
  const pricing = product?.pricing;
  if (!Array.isArray(pricing) || pricing.length === 0) return false;
  const active = pricing.filter(isEntryActive);
  const rows = active.length ? active : pricing;
  const now = new Date();
  return rows.every((entry) => !isPricingEntryAvailable(entry, now));
};

// True when a single variation option (e.g. "Red") is unavailable across every combo containing it.
// Matches by the option's position in product.variations against the comma-joined variationKey.
export const isVariationValueOutOfStock = (product, variationName, value) => {
  const pricing = product?.pricing;
  const variations = product?.variations;
  if (!Array.isArray(pricing) || pricing.length === 0 || !Array.isArray(variations)) return false;
  const idx = variations.findIndex((v) => v.variationName === variationName);
  if (idx < 0) return false;
  const now = new Date();
  const target = String(value ?? '').trim();
  const matching = pricing.filter((entry) => {
    if (!isEntryActive(entry)) return false;
    const parts = (entry.variationKey || '').split(',').map((p) => p.trim());
    return parts[idx] === target;
  });
  if (matching.length === 0) return false;
  return matching.every((entry) => !isPricingEntryAvailable(entry, now));
};
